import React, { useEffect, useState } from 'react';
import { filesize } from 'filesize';
import Spinner from './Spinner';
import { useServices } from 'services';

const Statistic = ({ number, name }: { number: number | undefined, name: string }): React.ReactElement => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '6rem',
            flexShrink: '0',
        }}
    >
        {number === undefined
            ? <Spinner />
            : (
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold', textAlign: 'center' }}>
                    {number}
                </span>
            )
        }
        <span
            style={{
                fontSize: '1rem',
                color: '#364153',
                fontWeight: 'bold',
                textAlign: 'center',
                textTransform: 'capitalize',
            }}
        >
            {name}
        </span>
    </div>
);

interface StatisticsProps {
    project: string;
    projectUploadsKey?: string;
    projectDatasetKey?: string;
    projectSearchKey?: string;
}

const Statistics = ({
    project,
    projectUploadsKey,
    projectDatasetKey,
    projectSearchKey,
}: StatisticsProps): React.ReactElement => {
    const { envService } = useServices();
    const [datasetCount, setDatasetCount] = useState<number | undefined>();
    const [downloadCount, setDownloadCount] = useState<number | undefined>();
    const [sizeValue, setSizeValue] = useState<number | undefined>();
    const [sizeUnits, setSizeUnits] = useState<string>('bytes');

    useEffect(() => {
        const base = envService.getValue('DATA_REPO_URL');
        // --- Datasets: derive from the `msdlive_project` facet aggregation -------
        const datasetPromise = projectSearchKey
            ? fetch(`${base}/api/records?size=1`)
                .then(r => r.json())
                .then(r => {
                    const buckets: Array<{ key: string; doc_count: number }> =
                        r?.aggregations?.msdlive_project?.buckets ?? [];
                    const bucket = buckets.find(b => b.key === projectSearchKey);
                    setDatasetCount(bucket?.doc_count ?? 0);
                })
            : fetch(`${base}/api/metrics/counts/datasets`)
                .then(r => r.json())
                .then(r => {
                    const key = projectDatasetKey ?? projectUploadsKey ?? project;
                    const total = r.reduce(
                        (sum: number, d: { [k: string]: number }) => sum + (d?.[key] ?? 0),
                        0,
                    );
                    setDatasetCount(total);
                });
        datasetPromise.catch(() => setDatasetCount(0));

        fetch(`${base}/api/metrics/uploads`).then(r => r.json()).then(r => {
            const bytesTotal = r.totalbytes.reduce(
                (b: number, d: { [k: string]: number }) =>
                    b + (d?.[projectUploadsKey ?? project] ?? 0),
                0,
            );
            const formatted = filesize(bytesTotal, {
                fullform: true,
                precision: 4,
                standard: 'si',
                output: 'array',
                roundingMethod: 'floor',
            }) as [number, string];
            setSizeValue(formatted[0]);
            setSizeUnits(formatted[1]);
        });

        fetch(`${base}/api/metrics/downloads`).then(r => r.json()).then(r => {
            const downloads = r.uniqueDatasets.reduce(
                (b: number, d: { [k: string]: number }) => b + (d?.[project] ?? 0),
                0,
            );
            setDownloadCount(downloads);
        });
    }, [project, projectUploadsKey, projectDatasetKey, projectSearchKey, envService]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-around',
                gap: '1rem',
            }}
        >
            <Statistic number={datasetCount} name='Datasets' />
            <Statistic number={downloadCount} name='Downloads' />
            <Statistic number={sizeValue} name={sizeUnits} />
        </div>
    );
};

export default Statistics;